import "zod/compile";
import { z } from "zod";

export const LEAD_TIME_SEMANTICS_VERSION = 1;
export const LEAD_TIME_PROVIDERS = [
  {
    id: "po_to_receipt",
    intervals: ["po_created:receipt_reported"],
    inputs: ["resolved_base"],
  },
] as const;

export const leadTimeComponentSourceSchema = z.discriminatedUnion("kind", [
  z.strictObject({
    kind: z.literal("static"),
    days: z.number().int().min(0).max(365),
  }),
  z.strictObject({
    kind: z.literal("calculated"),
    providerId: z.enum(["po_to_receipt"]),
  }),
]);
export type LeadTimeComponentSource = z.infer<
  typeof leadTimeComponentSourceSchema
>;

export const leadTimeComponentSchema = z.strictObject({
  id: z.string().min(1).max(64),
  label: z.string().min(1).max(120),
  sequence: z.number().int().nonnegative(),
  // Boundaries, rather than labels, establish sequential non-overlap.
  startsAt: z.string().min(1).max(64),
  endsAt: z.string().min(1).max(64),
  source: leadTimeComponentSourceSchema,
});
export type LeadTimeComponent = z.infer<typeof leadTimeComponentSchema>;

export function leadTimeSourceSupported(
  component: Pick<LeadTimeComponent, "startsAt" | "endsAt">,
  source: LeadTimeComponentSource,
): boolean {
  if (source.kind === "static") return true;
  return LEAD_TIME_PROVIDERS.some(
    (provider) =>
      provider.id === source.providerId &&
      provider.intervals.some(
        (interval) => interval === `${component.startsAt}:${component.endsAt}`,
      ),
  );
}

function validateComponentInterval(
  component: LeadTimeComponent,
  previous: LeadTimeComponent | undefined,
  boundaries: ReadonlySet<string>,
  ctx: z.RefinementCtx,
): void {
  if (
    component.startsAt === component.endsAt ||
    boundaries.has(component.endsAt)
  )
    ctx.addIssue({
      code: "custom",
      message: "Component intervals overlap or contain a cycle",
    });
  if (previous && previous.endsAt !== component.startsAt)
    ctx.addIssue({
      code: "custom",
      message:
        "Component intervals must form a sequential, non-overlapping chain",
    });
}

export const leadTimeProfileSchema = z
  .strictObject({
    profileId: z.string().min(1).max(64),
    version: z.number().int().positive(),
    applicability: z.literal("vendor_buy"),
    components: z.array(leadTimeComponentSchema).min(1).max(20),
  })
  .superRefine((profile, ctx) => {
    const ordered = [...profile.components].sort(
      (a, b) => a.sequence - b.sequence,
    );
    const ids = new Set<string>();
    const sequences = new Set<number>();
    const boundaries = new Set<string>();
    for (const [index, component] of ordered.entries()) {
      if (ids.has(component.id) || sequences.has(component.sequence))
        ctx.addIssue({
          code: "custom",
          message: "Component identities and sequences must be unique",
        });
      validateComponentInterval(component, ordered[index - 1], boundaries, ctx);
      if (!leadTimeSourceSupported(component, component.source))
        ctx.addIssue({
          code: "custom",
          message: "Provider is not supported for this interval",
        });
      ids.add(component.id);
      sequences.add(component.sequence);
      boundaries.add(component.startsAt);
      boundaries.add(component.endsAt);
    }
  });
export type LeadTimeProfile = z.infer<typeof leadTimeProfileSchema>;

export const DEFAULT_LEAD_TIME_PROFILE: LeadTimeProfile = {
  profileId: "vendor_buy",
  version: 1,
  applicability: "vendor_buy",
  components: [
    {
      id: "pre_order",
      label: "Pre-order processing",
      sequence: 0,
      startsAt: "need_recognized",
      endsAt: "po_created",
      source: { kind: "static", days: 7 },
    },
    {
      id: "po_to_receipt",
      label: "PO creation to receipt",
      sequence: 1,
      startsAt: "po_created",
      endsAt: "receipt_reported",
      source: { kind: "calculated", providerId: "po_to_receipt" },
    },
    {
      id: "receiving",
      label: "Receiving / putaway",
      sequence: 2,
      startsAt: "receipt_reported",
      endsAt: "stock_available",
      source: { kind: "static", days: 2 },
    },
  ],
};

export const leadTimeComponentOverrideSchema = z.strictObject({
  id: z.uuid(),
  componentId: z.string().min(1),
  productV3Key: z.string().min(1),
  warehouseId: z.uuid(),
  source: leadTimeComponentSourceSchema,
  reason: z.string().trim().min(1).max(2000),
  createdByUserId: z.uuid(),
  createdAt: z.iso.datetime(),
  startsAt: z.iso.datetime(),
  endsAt: z.iso.datetime().nullable(),
  reviewAt: z.iso.datetime().nullable(),
  deactivatedAt: z.iso.datetime().nullable(),
  deactivatedByUserId: z.uuid().nullable(),
  supersedesOverrideId: z.uuid().nullable(),
});
export type LeadTimeComponentOverride = z.infer<
  typeof leadTimeComponentOverrideSchema
>;
export const leadTimeSettingsSchema = z.strictObject({
  profile: leadTimeProfileSchema.nullable(),
  overrides: z.array(leadTimeComponentOverrideSchema),
});
export type LeadTimeSettings = z.infer<typeof leadTimeSettingsSchema>;

export const leadTimeCompositionSnapshotSchema = z.object({
  semanticsVersion: z.literal(1),
  settingsFingerprint: z.string(),
  profileId: z.string().nullable(),
  profileVersion: z.number().nullable(),
  status: z.enum([
    "composed",
    "legacy",
    "total_override",
    "excluded",
    "unresolved",
  ]),
  exclusionReason: z.string().nullable(),
  baseDays: z.number().nullable(),
  baseSource: z.string(),
  effectiveDays: z.number().nullable(),
  sampleN: z.number().nonnegative(),
  sigmaDays: z.number().nullable(),
  totalOverrideId: z.string().nullable(),
  context: z.object({
    productV3Key: z.string(),
    warehouseId: z.string(),
    sourceType: z.string().nullable(),
    sourceWarehouseId: z.string().nullable(),
    supplierId: z.string().nullable(),
    shipToWarehouseId: z.string().nullable(),
  }),
  components: z.array(
    z.object({
      id: z.string(),
      label: z.string(),
      sequence: z.number(),
      startsAt: z.string(),
      endsAt: z.string(),
      source: leadTimeComponentSourceSchema,
      days: z.number().nullable(),
      quality: z.enum([
        "configured",
        "measured",
        "declared",
        "assumed",
        "unresolved",
      ]),
      owner: z.enum(["organization", "sku_warehouse"]),
      overrideId: z.string().nullable(),
      reason: z.string().nullable(),
      createdByUserId: z.string().nullable(),
      endsAtOverride: z.string().nullable(),
      reviewAt: z.string().nullable(),
    }),
  ),
});
export type LeadTimeCompositionSnapshot = z.infer<
  typeof leadTimeCompositionSnapshotSchema
>;
